import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { registerFocusable } from '@tamagui/focusable';
import { Check, ChevronDown, Pointer, X } from '@tamagui/lucide-icons';
import { Pressable, PressableProps } from '@unistocks/components/basic/pressable';
import { Button } from '@unistocks/components/button/button';
import { Row } from '@unistocks/components/layout/flex';
import { EmptyList } from '@unistocks/components/no-data/empty-list';
import { NoDataProps } from '@unistocks/components/no-data/no-data';
import { SelectHeader } from '@unistocks/components/select/header';
import { ItemSeparator } from '@unistocks/components/select/item-separator';
import { SelectTrigger, SelectTriggerProps } from '@unistocks/components/select/trigger';
import { extractValueKey } from '@unistocks/components/select/value-key';
import { Sheet, SheetProps } from '@unistocks/components/sheet/sheet';
import { useSheetContainerStyles } from '@unistocks/components/sheet/utils';
import { Heading } from '@unistocks/components/text/text';
import { ListRenderItem } from '@unistocks/components/virtual-list/list';
import { SectionList, SectionListProps } from '@unistocks/components/virtual-list/section-list';
import { t } from '@unistocks/localization';
import { triggerHaptics } from '@unistocks/shared/utils/haptics/haptics';
import { arrayify, difference, isDefined, union } from '@unistocks/shared/utils/util';
import { PropsWithRef, SelectOption } from '@unistocks/types';
import { useEffect, useMemo, useState } from 'react';
import { GestureResponderEvent } from 'react-native';
import { isWeb, ListItem, Square, useControllableState, useMedia, View } from 'tamagui';

type BasicTriggerProps = {
  trigger?: undefined;
  /**
   * Props for the trigger container element. If you want to use a custom trigger, use the `trigger` prop instead.
   */
  triggerProps?: SelectTriggerProps;
};

type CustomTriggerProps = {
  /* Custom trigger element */
  trigger: React.ReactNode;
  /**
   * Props for the trigger container element. If you want to use the default trigger, use the `trigger` prop as undefined.
   */
  triggerProps?: PressableProps;
};

// TODO: Type TriggerProps based on type of trigger
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TriggerProps = BasicTriggerProps | CustomTriggerProps;

type BaseSelectProps<TItem = any> = PropsWithRef<
  Omit<SheetProps, 'onChange' | 'children' | 'ref'>,
  typeof SelectTrigger
> & {
  id?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  options: ReadonlyArray<SelectOption<TItem>>;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  invalid?: boolean;
  valueKey?: string;
  groupBy?: string;
  renderGroupHeader?: (group: string, index: number) => string;
  /**
   * If true, the select will close when an option is selected. Only applicable when `multiple` is false.
   */
  closeOnSelect?: boolean;
  keyExtractor?: (item: SelectOption<TItem>, index: number) => string;
  triggerProps?: PressableProps | SelectTriggerProps;
  /* Custom trigger element */
  trigger?: React.ReactNode;
  focus?: () => void;
  /**
   * If true, system colors (status bar, navigation bar) will be controlled by the open state of the sheet
   * @default false
   */
  controlSystemColors?: boolean;
  noDataText?: NoDataProps['text'];
  noDataIcon?: NoDataProps['Icon'];
  noDataColor?: NoDataProps['color'];
  noDataSize?: NoDataProps['size'];
};

export type SingleSelectProps<TItem> = BaseSelectProps<TItem> & {
  multiple?: false;
};

export type MultiSelectProps<TItem> = BaseSelectProps<TItem> & {
  multiple: true;
  value: TItem[];
  onChange: (value: TItem[]) => void;
};

export type RequiredSingleSelectProps<TItem> = SingleSelectProps<TItem> & {
  isRequired: true;
  value: TItem;
  onChange: (value: TItem) => void;
};

export type OptionalSingleSelectProps<TItem> = SingleSelectProps<TItem> & {
  isRequired?: false;
  value: TItem | null;
  onChange: (value: TItem | null) => void;
};

export type SelectRef = SelectProps['ref'];

export type SelectProps<TItem = any> =
  MultiSelectProps<TItem> | RequiredSingleSelectProps<TItem> | OptionalSingleSelectProps<TItem>;

export function Select<TItem = any>({
  ref,
  id,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  options,
  multiple,
  placeholder,
  value: inputValue,
  isRequired,
  isDisabled,
  invalid,
  groupBy,
  valueKey,
  renderGroupHeader,
  closeOnSelect = true,
  keyExtractor: keyExtractorProp,
  onChange,
  triggerProps,
  trigger,
  headerTitle,
  headerIcon,
  headerRight: headerRightProp,
  noDataText,
  noDataIcon,
  noDataColor,
  noDataSize = '$5',
  ...sheetProps
}: SelectProps<TItem>) {
  const BottomSheetScrollComponent = useBottomSheetScrollableCreator();
  const contentContainerStyle = useSheetContainerStyles();
  const media = useMedia();
  const value = useMemo(() => (isDefined(inputValue) ? arrayify(inputValue) : []), [inputValue]);
  const [pendingValue, setPendingValue] = useState<TItem[]>(() => (multiple ? [...value] : []));
  const effectiveValue = multiple ? pendingValue : value;

  const displayValue = value.length
    ? value
        .map(x => {
          const item = options.find(({ value }, i) =>
            valueKey ? extractValueKey(value, valueKey) === x : value === x
          );

          return item?.title ?? item?.key;
        })
        .filter(Boolean)
        .join(', ')
    : (placeholder ?? t('::Select'));

  const data = useMemo(
    () =>
      groupBy
        ? groupSelectItems(options, groupBy as keyof TItem).reduce(
            (acc, [key, data]) => [...acc, key, ...data],
            [] as SectionListProps<SelectOption<TItem>>['data']
          )
        : options,
    [groupBy, options]
  );

  const [open, onOpenChange] = useControllableState({
    defaultProp: false,
    prop: openProp,
    onChange: val => {
      if (val && multiple) {
        setPendingValue(value);
      }
      onOpenChangeProp?.(val);
    }
  });

  const keyExtractor: SectionListProps<SelectOption<TItem>>['keyExtractor'] = (item, index) =>
    typeof item === 'string' ? item : (keyExtractorProp?.(item, index) ?? (item.value as string));

  const handleSubmit = () => {
    (onChange as (value: TItem[]) => void)(pendingValue);
    onOpenChange(false);
  };

  useEffect(() => {
    if (!id || isDisabled) return;

    return registerFocusable(id, {
      focus: () => onOpenChange(true)
    });
  }, [id, isDisabled, onOpenChange]);

  const handleSelection = (item: SelectOption<TItem>, index: number, e: GestureResponderEvent) => {
    triggerHaptics('selection');

    const itemValue = valueKey ? extractValueKey(item.value, valueKey) : item.value;

    if (multiple) {
      setPendingValue(prev =>
        prev.includes(itemValue) ? difference(prev, itemValue) : union(prev, itemValue)
      );
      return;
    }

    if (closeOnSelect) onOpenChange(false);

    if (!value.includes(itemValue)) onChange(itemValue);
  };

  const renderItem: ListRenderItem<SelectOption<TItem>> = ({ item, index }) => {
    const itemValue = valueKey ? extractValueKey(item.value, valueKey) : item.value;
    const isSelected = effectiveValue.includes(itemValue);

    return (
      <ListItem
        cursor="pointer"
        size={'$5'}
        $md={{ size: '$4' }}
        gap={'$3'}
        role="listitem"
        tabIndex={0}
        accessible
        icon={item.icon}
        scaleIcon={1.5}
        aria-selected={isSelected}
        onPress={e => handleSelection(item, index, e)}
      >
        <ListItem.Text
          size={'$6'}
          color={isSelected ? '$color' : '$bodyFont'}
          fontWeight={isSelected ? '$7' : undefined}
        >
          {item.title ?? item.key}
        </ListItem.Text>

        <View transition={'quicker'} o={isSelected ? 1 : 0}>
          <Check testID="selected-check" color={'$success'} />
        </View>
      </ListItem>
    );
  };

  const renderSectionHeader: NonNullable<SectionListProps<TItem>['renderSectionHeader']> = ({
    item,
    index
  }) => {
    return (
      <Heading size={'$7'} textAlign="center" py={'$2'} bg={'$background'}>
        {renderGroupHeader?.(item, index) ?? item}
      </Heading>
    );
  };

  const headerRight = multiple ? (
    <Row ai={'center'}>
      {headerRightProp}
      <Button testID="select-submit-button" size={'$2'} onPress={handleSubmit}>
        {t('::Save')}
      </Button>
    </Row>
  ) : (
    headerRightProp
  );

  const renderTrigger = () => {
    if (trigger) {
      return (
        <Pressable
          id={id}
          testID="select-trigger"
          disabled={isDisabled}
          onPress={() => onOpenChange?.(true)}
          onFocus={() => onOpenChange?.(true)}
          {...triggerProps}
        >
          {trigger}
        </Pressable>
      );
    }

    const onKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isDisabled) {
        e.preventDefault();
        onOpenChange?.(true);
      }
    };

    return (
      <SelectTrigger
        ref={ref}
        id={id}
        testID="select-trigger"
        disabled={isDisabled}
        invalid={invalid}
        iconAfter={({ size, color }) => (
          <Row>
            {!isRequired && value.length ? (
              <X
                testID="select-clear-icon"
                color={'$danger'}
                size={size}
                hitSlop={{ bottom: 12, left: 12, right: 12, top: 12 }}
                onPress={e => {
                  e.stopPropagation();
                  onChange?.(null as any);
                }}
              />
            ) : null}
            <Square transition={'quick'} rotate={open ? '180deg' : '0deg'}>
              <ChevronDown size={size} color={color} />
            </Square>
          </Row>
        )}
        onPress={e => onOpenChange?.(true)}
        {...(isWeb && { onKeyDown })}
        {...triggerProps}
      >
        <SelectTrigger.Text flex={1} placeholder={!value.length}>
          {displayValue}
        </SelectTrigger.Text>
      </SelectTrigger>
    );
  };

  return (
    <>
      {renderTrigger()}
      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        size="small"
        scrollMode="custom"
        headerTitle={headerTitle}
        headerIcon={headerIcon ?? (headerTitle ? <Pointer color={'$headingFont'} /> : undefined)}
        headerRight={headerRight}
        enableDynamicSizing={media.sm}
        {...sheetProps}
      >
        <SectionList
          testID="select-list"
          role="list"
          accessibilityRole="list"
          keyboardDismissMode={'interactive'}
          keyboardShouldPersistTaps={'handled'}
          mx={'$-3'}
          data={data}
          extraData={effectiveValue}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={contentContainerStyle as any}
          ListHeaderComponent={
            isRequired ? undefined : (
              <SelectHeader onChange={onChange} onOpenChange={onOpenChange} />
            )
          }
          ListEmptyComponent={
            <EmptyList
              mt={'$3'}
              noDataText={noDataText}
              noDataIcon={noDataIcon}
              color={noDataColor}
              size={noDataSize}
            />
          }
          ItemSeparatorComponent={ItemSeparator}
          renderScrollComponent={BottomSheetScrollComponent}
        />
      </Sheet>
    </>
  );
}

const groupSelectItems = <T = any,>(options: ReadonlyArray<SelectOption<T>>, groupBy: keyof T) => {
  if (!options?.length) return [];

  const groups = options.reduce((acc, item) => {
    const key = item.value[groupBy] as string;

    if (acc.has(key)) {
      acc.get(key)!.push(item);
    } else {
      acc.set(key, [item]);
    }

    return acc;
  }, new Map<string, SelectOption<T>[]>());

  return Array.from(groups);
};
